import React, { useEffect, useState, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import {
  Form,
  Row,
  Col,
  Divider,
  message,
  Modal,
  theme as antdTheme
} from 'antd';
import { useTranslation } from 'react-i18next';
import { RootState } from 'store/configureStore';
import { OptionType } from 'store/ducks/Global/types';
import { globalOp } from 'store/ducks/Global';
import CustomSelect from 'components/CustomSelect';
import WhoAmILayout from 'components/Layouts/WhoAmILayout/WhoAmILayout';
import ApiService from 'utils/ApiCalls';
import * as Styled from '../WhoAmI.styled';

const mapOptions = (data: any[] = []): OptionType[] =>
  data.map((item) => ({
    label: item.name,
    value: item.id
  }));

const mapIndustryOptions = (data: any[] = []): OptionType[] =>
  data.map((item) => ({
    label: item.industry,
    value: item.ind_id
  }));

const mapModuleOptions = (data: any[] = []): OptionType[] =>
  data.map((item) => ({
    label: item.mod_short_name,
    value: item.mod_id
  }));

const OrganizationalContext: React.FC = () => {
  const { t, i18n } = useTranslation();
  const dispatch = useDispatch();
  const prevValuesRef = useRef<any>({});
  const isHydratingRef = useRef(false);

  const { scheme, lang, orgContext } = useSelector((state: RootState) => ({
    scheme: state.global.scheme,
    lang: state.global.lang,
    orgContext: state.global.hierarchicalLevelsScope
  }));

  const { token } = antdTheme.useToken();

  const [groupOptions, setGroupOptions] = useState<OptionType[]>([]);
  const [divisionOptions, setDivisionOptions] = useState<OptionType[]>([]);
  const [regionOptions, setRegionOptions] = useState<OptionType[]>([]);
  const [legalEntityOptions, setLegalEntityOptions] = useState<OptionType[]>(
    []
  );
  const [businessUnitOptions, setBusinessUnitOptions] = useState<OptionType[]>(
    []
  );
  const [industryOptions, setIndustryOptions] = useState<OptionType[]>([]);
  const [moduleOptionsState, setModuleOptionsState] = useState<OptionType[]>(
    []
  );
  const [mainOrgOptions, setMainOrgOptions] = useState<OptionType[]>([]);

  const [loading, setLoading] = useState(false);
  const [saveDisabled, setSaveDisabled] = useState(false);

  const [form] = Form.useForm();

  useEffect(() => {
    i18n.changeLanguage(lang);
  }, [lang, i18n]);

  /* ---------------- watchers ---------------- */
  const group = Form.useWatch('group', form);
  const division = Form.useWatch('division', form);
  const region = Form.useWatch('region', form);
  const legalEntity = Form.useWatch('legalEntity', form);
  const businessUnit = Form.useWatch('businessUnit', form);
  const industryType = Form.useWatch('industryType', form) || [];
  const module = Form.useWatch('module', form);
  const mainOrganization = Form.useWatch('mainOrganization', form);

  const hasHierarchySelection =
    group?.length ||
    division?.length ||
    region?.length ||
    legalEntity?.length ||
    businessUnit?.length;

  /* ---------------- dropdown fetch ---------------- */
  useEffect(() => {
    const fetchDropdownOptions = async () => {
      try {
        const [
          groups,
          divisions,
          regions,
          legalEntities,
          businessUnits,
          mainOrgs
        ] = await Promise.all([
          ApiService.getGroupBPR(),
          ApiService.getDivisionBPR(),
          ApiService.getRegionBPR(),
          ApiService.getLegalEntitiesBPR(),
          ApiService.getBusinessUnitBPR(),
          ApiService.getMainOrganizations()
        ]);

        setGroupOptions(mapOptions(groups?.data?.data));
        setDivisionOptions(mapOptions(divisions?.data?.data));
        setRegionOptions(mapOptions(regions?.data?.data));
        setLegalEntityOptions(mapOptions(legalEntities?.data?.data));
        setBusinessUnitOptions(mapOptions(businessUnits?.data?.data));
        setMainOrgOptions(mapOptions(mainOrgs?.data?.data));
      } catch (err) {
        console.error(err);
      }
    };

    fetchDropdownOptions();
  }, []);

  /* ---------------- hydrate ---------------- */
  useEffect(() => {
    if (!orgContext) return;

    isHydratingRef.current = true;

    form.setFieldsValue({
      group: orgContext.group || [],
      division: orgContext.division || [],
      region: orgContext.region || [],
      legalEntity: orgContext.legalEntity || [],
      businessUnit: orgContext.businessUnit || [],
      industryType: orgContext.industryType || [],
      module: orgContext.module || []
    });

    const timer = setTimeout(() => {
      isHydratingRef.current = false;
    }, 500);

    return () => clearTimeout(timer);
  }, [orgContext]);

  /* ---------------- industries ---------------- */
  useEffect(() => {
    const fetchIndustries = async () => {
      if (!hasHierarchySelection) {
        setIndustryOptions([]);
        setModuleOptionsState([]);

        if (!isHydratingRef.current) {
          form.setFieldsValue({ industryType: [], module: [] });
        }
        return;
      }

      const res = await ApiService.getIndustriesByHierarchy({
        group_id: group,
        division_id: division,
        region_id: region,
        legal_entity_id: legalEntity,
        business_unit_id: businessUnit,
        lang
      });

      const mapped = mapIndustryOptions(res?.data?.data ?? []);
      setIndustryOptions(mapped);

      const valid = new Set(mapped.map((i) => i.value));

      const current: any[] = form.getFieldValue('industryType') || [];

      const filtered = current.filter((id: any) => {
        const value = typeof id === 'object' ? id.value : id;
        return valid.has(value);
      });

      if (filtered.length !== current.length) {
        form.setFieldsValue({
          industryType: filtered,
          module: []
        });
      }
    };

    fetchIndustries();
  }, [group, division, region, legalEntity, businessUnit, lang]);

  /* ---------------- modules ---------------- */
  useEffect(() => {
    const fetchModules = async () => {
      if (!industryType?.length) {
        setModuleOptionsState([]);

        if (!isHydratingRef.current) {
          form.setFieldsValue({ module: [] });
        }
        return;
      }

      const ids = industryType.map((item: any) =>
        typeof item === 'object' ? item.value : item
      );

      const responses = await Promise.all(
        ids.map((id: number) =>
          ApiService.getModulesByIndustries({ lang, ind_id: id })
        )
      );

      const merged = responses.flatMap((r) => r?.data?.data ?? []);

      const unique = Array.from(
        new Map(merged.map((m: any) => [m.mod_id, m])).values()
      );

      const mapped = mapModuleOptions(unique);
      setModuleOptionsState(mapped);

      if (isHydratingRef.current) return;

      const current = form.getFieldValue('module') || [];
      const valid = new Set(mapped.map((m) => m.value));

      form.setFieldsValue({
        module: current.filter((v: any) => {
          const value = typeof v === 'object' ? v.value : v;
          return valid.has(value);
        })
      });
    };

    fetchModules();
  }, [industryType, lang]);

  /* ---------------- main organization mutual exclusivity ---------------- */
  useEffect(() => {
    if (mainOrganization && !isHydratingRef.current) {
      form.setFieldsValue({
        group: [],
        division: [],
        region: [],
        legalEntity: [],
        businessUnit: [],
        industryType: [],
        module: []
      });
    }
  }, [mainOrganization]);

  /* ---------------- save ---------------- */
  const normalizeArray = (val: any) => {
    if (!val) return [];
    if (Array.isArray(val)) return val.map((v) => v?.value ?? v);
    if (typeof val === 'object') return [val.value];
    return [val];
  };

  const handleSave = async () => {
    try {
      const values = await form.validateFields();

      setLoading(true);
      setSaveDisabled(true);

      const normalized = {
        group: normalizeArray(values.group),
        division: normalizeArray(values.division),
        region: normalizeArray(values.region),
        legalEntity: normalizeArray(values.legalEntity),
        businessUnit: normalizeArray(values.businessUnit),
        industryType: values.industryType || [],
        module: normalizeArray(values.module)
      };

      dispatch(globalOp.setHierarchicalLevelsScope(normalized));
      localStorage.setItem('orgContext', JSON.stringify(normalized));

      const mainOrgValue = values.mainOrganization || null;
      dispatch(globalOp.setMainOrganization(mainOrgValue));
      mainOrgValue
        ? localStorage.setItem('mainOrganization', JSON.stringify(mainOrgValue))
        : localStorage.removeItem('mainOrganization');

      message.success(t('ORG_CONTEXT_SAVE_SUCCESS'));
    } catch {
      message.error(t('ORG_CONTEXT_SAVE_FAILED'));
    } finally {
      setLoading(false);
      setSaveDisabled(false);
    }
  };

  /* ---------------- render labels ---------------- */
  const renderSelectedLabels = (values: any[], options: OptionType[]) => {
    if (!values?.length) return '-';

    return values
      .map((v) => {
        if (typeof v === 'object') {
          return v.label;
        }

        return options.find((o) => o.value === v)?.label || v;
      })
      .join(' • ');
  };
  /* ---------------- impact check ---------------- */
  const getImpact = async (nextValues: any) => {
    const res = await ApiService.getIndustriesByHierarchy({
      group_id: nextValues.group,
      division_id: nextValues.division,
      region_id: nextValues.region,
      legal_entity_id: nextValues.legalEntity,
      business_unit_id: nextValues.businessUnit,
      lang
    });

    const industries = res?.data?.data ?? [];
    const valid = new Set(industries.map((i: any) => i.ind_id));

    const current = form.getFieldValue('industryType') || [];

    return {
      willRemoveIndustry: current.some((id: any) => {
        const value = typeof id === 'object' ? id.value : id;
        return !valid.has(value);
      })
    };
  };

  /* ---------------- hierarchy change ---------------- */
  const handleHierarchyChange = async (_: any, allValues: any) => {
    const impact = await getImpact(allValues);

    if (!impact.willRemoveIndustry) {
      prevValuesRef.current = allValues;
      return;
    }

    Modal.confirm({
      title: t('ORG_CONTEXT_HIERARCHY_CHANGE_TITLE'),
      content: t('ORG_CONTEXT_HIERARCHY_CHANGE_CONTENT'),
      okText: t('YES'),
      cancelText: t('NO'),

      onCancel: () => {
        form.setFieldsValue(prevValuesRef.current);
      }
    });
  };

  useEffect(() => {
    prevValuesRef.current = form.getFieldsValue(true);
  }, []);

  /* ---------------- UI ---------------- */
  return (
    <WhoAmILayout>
      <Styled.Container>
        <Styled.SelectedFiltersSection scheme={scheme} token={token}>
          <Styled.SelectedFiltersHeader>
            <Styled.SelectedFiltersHeading>
              <div>
                <Styled.SelectedFiltersTitle token={token}>
                  {t('SELECTED_FILTERS')}
                </Styled.SelectedFiltersTitle>
              </div>
            </Styled.SelectedFiltersHeading>
          </Styled.SelectedFiltersHeader>

          <Styled.SelectedContextBox scheme={scheme}>
            <Styled.ContextItem>
              <strong>{t('GROUP')}</strong>
              <span>{renderSelectedLabels(group, groupOptions)}</span>
            </Styled.ContextItem>

            <Styled.ContextItem>
              <strong>{t('DIVISION')}</strong>
              <span>{renderSelectedLabels(division, divisionOptions)}</span>
            </Styled.ContextItem>

            <Styled.ContextItem>
              <strong>{t('REGION')}</strong>
              <span>{renderSelectedLabels(region, regionOptions)}</span>
            </Styled.ContextItem>

            <Styled.ContextItem>
              <strong>{t('LEGAL_ENTITY')}</strong>
              <span>
                {renderSelectedLabels(legalEntity, legalEntityOptions)}
              </span>
            </Styled.ContextItem>

            <Styled.ContextItem>
              <strong>{t('BUSINESSUNIT')}</strong>
              <span>
                {renderSelectedLabels(businessUnit, businessUnitOptions)}
              </span>
            </Styled.ContextItem>

            <Styled.ContextItem>
              <strong>{t('MAIN_ORGANIZATIONS')}</strong>
              <span>
                {mainOrganization
                  ? mainOrgOptions.find((o) => o.value === mainOrganization)
                      ?.label || mainOrganization
                  : '-'}
              </span>
            </Styled.ContextItem>

            <Styled.ContextItem>
              <strong>{t('INDUSTRY')}</strong>
              <span>{renderSelectedLabels(industryType, industryOptions)}</span>
            </Styled.ContextItem>

            <Styled.ContextItem>
              <strong>{t('MODULE')}</strong>
              <span>{renderSelectedLabels(module, moduleOptionsState)}</span>
            </Styled.ContextItem>
          </Styled.SelectedContextBox>
        </Styled.SelectedFiltersSection>

        <Divider />

        <Styled.Title scheme={scheme}>{t('CHANGE_OF_FOCUS_AREA')}</Styled.Title>

        <Form
          form={form}
          layout='vertical'
          onValuesChange={handleHierarchyChange}
        >
          <Row gutter={[16, 16]} wrap>
            {[
              { name: 'group', label: t('GROUP'), options: groupOptions },
              {
                name: 'division',
                label: t('DIVISION'),
                options: divisionOptions
              },
              { name: 'region', label: t('REGION'), options: regionOptions },
              {
                name: 'legalEntity',
                label: t('LEGAL_ENTITY'),
                options: legalEntityOptions
              },
              {
                name: 'businessUnit',
                label: t('BUSINESSUNIT'),
                options: businessUnitOptions
              },
              {
                name: 'mainOrganization',
                label: t('Main Organization'),
                options: mainOrgOptions,
                isMainOrg: true
              }
            ].map((f) => (
              <Col xs={12} sm={8} md={6} lg={4} xl={3} key={f.name}>
                <Form.Item name={f.name} label={f.label}>
                  {f.isMainOrg ? (
                    <CustomSelect
                      options={f.options}
                      disabled={hasHierarchySelection}
                      placeholder={
                        hasHierarchySelection
                          ? t('DISABLED_WHEN_HIERARCHY_SELECTED')
                          : t('')
                      }
                      allowClear
                    />
                  ) : (
                    <CustomSelect mode='multiple' options={f.options} />
                  )}
                </Form.Item>
              </Col>
            ))}
          </Row>

          <Row gutter={[16, 16]}>
            <Col xs={12} sm={8} md={6} lg={4} xl={3}>
              <Form.Item name='industryType' label={t('INDUSTRY_TYPE')}>
                <CustomSelect
                  mode='multiple'
                  options={industryOptions}
                  labelInValue
                />
              </Form.Item>
            </Col>

            <Col xs={12} sm={8} md={6} lg={4} xl={3}>
              <Form.Item name='module' label={t('MODULE')}>
                <CustomSelect mode='multiple' options={moduleOptionsState} />
              </Form.Item>
            </Col>

            <Col xs={12} sm={8} md={6} lg={4} xl={3}>
              <Form.Item label=' '>
                <Styled.ApplyButton
                  type='primary'
                  onClick={handleSave}
                  loading={loading}
                  disabled={saveDisabled}
                >
                  {t('SAVE')}
                </Styled.ApplyButton>
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Styled.Container>
    </WhoAmILayout>
  );
};

export default OrganizationalContext;
